import { cn } from '@/lib/utils'
import { ChevronRight, MoreHorizontal } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * Figma MCP (nodes 111:1134, 111:1136):
 * Link item: font-normal text-muted-foreground, hover:text-foreground
 * Current item: font-normal text-foreground (not muted)
 * Icon variant: gap-2, icon size-[18px], text overflow-hidden text-ellipsis whitespace-nowrap
 * Separator: ChevronRight
 * Sizes: md (text-xl/20px) and sm (text-sm/14px)
 */
export interface BreadcrumbItem {
  /** Display label */
  label: ReactNode
  /** Link href — last item (current page) should not have href */
  href?: string
  /**
   * Client-side navigation handler. Prefer this over `href` inside the SPA:
   * `href` renders a real anchor, so the browser does a FULL PAGE RELOAD, which
   * re-runs Keycloak's check-sso handshake and reads as a dead click while it
   * round-trips. Takes precedence over `href` when both are given.
   */
  onSelect?: () => void
  /** Optional icon before label */
  icon?: ReactNode
}

export interface IntuiBreadcrumbProps {
  /** Breadcrumb items, last item is treated as current page */
  items: BreadcrumbItem[]
  /** Custom separator element (default: ChevronRight) */
  separator?: ReactNode
  /** Max items to show before truncating with ellipsis */
  maxItems?: number
  /** Additional className */
  className?: string
}

function IntuiBreadcrumb({
  items,
  separator,
  maxItems,
  className,
}: IntuiBreadcrumbProps) {
  const separatorEl = separator ?? (
    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
  )

  let visibleItems = items
  let showEllipsis = false

  if (maxItems && items.length > maxItems && maxItems >= 3) {
    // Show first, ellipsis, last (maxItems - 2) items
    const tail = maxItems - 1
    visibleItems = [items[0], ...items.slice(items.length - tail)]
    showEllipsis = true
  }

  return (
    <nav aria-label="Breadcrumb" className={cn('flex', className)}>
      <ol className="flex items-center gap-1.5 text-sm">
        {visibleItems.map((item, index) => {
          const isLast = index === visibleItems.length - 1
          const isFirst = index === 0

          return (
            <li key={index} className="flex items-center gap-1.5">
              {/* Separator (not before first item) */}
              {index > 0 && (
                <span className="flex items-center" aria-hidden="true">
                  {separatorEl}
                </span>
              )}

              {/* Ellipsis after first item */}
              {showEllipsis && isFirst && index === 0 && (
                <>
                  <span className="flex items-center" aria-hidden="true">
                    {separatorEl}
                  </span>
                  <span className="flex h-9 w-9 items-center justify-center">
                    <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                    <span className="sr-only">More</span>
                  </span>
                </>
              )}

              {/* Item */}
              {isLast ? (
                <span
                  className="font-normal text-foreground"
                  aria-current="page"
                >
                  {item.icon && (
                    <span className="mr-1 inline-flex items-center">
                      {item.icon}
                    </span>
                  )}
                  {item.label}
                </span>
              ) : item.onSelect ? (
                <button
                  type="button"
                  onClick={item.onSelect}
                  // focus-visible is explicit: the global button reset drops
                  // the native outline, so without it a keyboard user cannot
                  // see which crumb is focused before activating it.
                  className="rounded-sm font-normal text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {item.icon && (
                    <span className="mr-1 inline-flex items-center">
                      {item.icon}
                    </span>
                  )}
                  {item.label}
                </button>
              ) : item.href ? (
                <a
                  href={item.href}
                  // focus-visible is explicit: the global button reset drops
                  // the native outline, so without it a keyboard user cannot
                  // see which crumb is focused before activating it.
                  className="rounded-sm font-normal text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {item.icon && (
                    <span className="mr-1 inline-flex items-center">
                      {item.icon}
                    </span>
                  )}
                  {item.label}
                </a>
              ) : (
                <span className="font-normal text-muted-foreground">
                  {item.icon && (
                    <span className="mr-1 inline-flex items-center">
                      {item.icon}
                    </span>
                  )}
                  {item.label}
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

export { IntuiBreadcrumb }
